import { Button } from '@app/components/various/Button';
import { AddCircleOutline, Remove } from '@app/components/various/Icons';
import { SectionCard } from '@app/components/various/SectionCard';
import { Skeleton } from '@app/components/various/Skeleton';
import { Sortable } from '@app/components/various/Sortable';
import { useAvailableExportFields } from '@app/services/hooks/documents/useAvailableExportFields';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Sortable as SortableNs } from '../../../../../typings';

import { DownloadProductInformationFormValues } from './DownloadProductInformationModal';

interface Props {
  isDisabled?: boolean;
}

export const DownloadProductInformationFields = ({ isDisabled = false }: Props) => {
  const { t } = useTranslation(['common', 'orders']);
  const { data: exportFields, isLoading, isError } = useAvailableExportFields();
  const { getFieldState } = useFormContext();
  const {
    field: { value, onChange, onBlur },
  } = useController<DownloadProductInformationFormValues, 'fields'>({ name: 'fields' });

  React.useEffect(() => {
    if (!exportFields) {
      return;
    }

    const activatedFieldIds = exportFields.filter(field => field.isActivated).map(field => field.key);
    onChange(activatedFieldIds);
    onBlur();
  }, [exportFields, onBlur, onChange]);

  const availableFieldIds = React.useMemo(() => {
    if (!exportFields || !getFieldState('fields').isTouched) {
      return [];
    }

    return exportFields.filter(field => !value.some(key => field.key === key)).map(field => field.key);
  }, [exportFields, getFieldState, value]);

  const handleAddAllFields = () => {
    onChange([...value, ...availableFieldIds]);
  };

  const handleRemoveAllFields = () => {
    onChange([]);
  };

  const handleSortEnd = (sortedFields: SortableNs.Items<string>) => {
    if (isDisabled) {
      return;
    }

    onChange(sortedFields.map(field => field.id));
  };

  const handleAddField = (key: string) => {
    onChange([...value, key]);
  };

  const handleRemoveField = (key: string) => {
    onChange(value.filter(fieldKey => fieldKey !== key));
  };

  const renderAvailableField = (key: string) => {
    const name = exportFields?.find(field => field.key === key)?.value ?? '';

    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={isDisabled}
          aria-label={t('common:add_value', { value: name })}
          className="rounded-full absolute-cursor-pointer"
          onClick={() => handleAddField(key)}
        >
          <AddCircleOutline className="size-4" />
        </button>
        <span className="body-4">{name}</span>
      </div>
    );
  };

  const renderAddedField = (key: string) => {
    const name = exportFields?.find(field => field.key === key)?.value ?? '';

    return (
      <div className="group/field flex w-full items-center justify-between gap-2">
        <span className="body-4">{name}</span>
        <Button
          aria-label={t('common:remove_value', { name })}
          icon={Remove}
          disabled={isDisabled}
          variant="text"
          color="danger"
          className="opacity-0 group-focus-within/field:opacity-100 group-hover/field:opacity-100 touch:opacity-100"
          onClick={() => handleRemoveField(key)}
        />
      </div>
    );
  };

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="grid gap-5 md:grid-cols-2">
        <Skeleton type="rectangle" className="h-96" />
        <Skeleton type="rectangle" className="h-96" />
      </div>
    );
  }

  return (
    <div className="grid gap-5 md:grid-cols-2">
      <SectionCard>
        <div className="mb-5 flex min-h-8 items-center justify-between gap-4">
          <h3 className="heading-3">{t('orders:available_attributes')}</h3>
          {!isEmpty(availableFieldIds) && (
            <Button size="medium" variant="bordered" color="secondary" disabled={isDisabled} onClick={handleAddAllFields}>
              {t('common:add_all')}
            </Button>
          )}
        </div>
        <div className="max-h-72 min-h-16 overflow-y-auto">
          {isEmpty(availableFieldIds) && <p className="text-neutral-40 body-2">{t('orders:available_attributes_empty_hint')}</p>}
          <Sortable isDisabled items={availableFieldIds.map(id => ({ id }))} renderItem={renderAvailableField} />
        </div>
      </SectionCard>
      <SectionCard>
        <div className="mb-5 flex min-h-8 items-center justify-between gap-4">
          <h3 className="heading-3">{t('orders:added_attributes')}</h3>
          {!isEmpty(value) && (
            <Button size="medium" variant="bordered" color="secondary" disabled={isDisabled} onClick={handleRemoveAllFields}>
              {t('common:remove_all')}
            </Button>
          )}
        </div>
        <div className="max-h-72 min-h-16 overflow-y-auto">
          {isEmpty(value) && <p className="text-neutral-40 body-2">{t('orders:added_attributes_empty_hint')}</p>}
          <Sortable items={value.map(id => ({ id }))} renderItem={renderAddedField} dragMode="handle" onSortEnd={handleSortEnd} />
        </div>
      </SectionCard>
    </div>
  );
};
